import {Component, OnInit} from '@angular/core';
import {ConfirmationService, MenuItem, SelectItem} from 'primeng/api';

import {classToPlain, plainToClass} from 'class-transformer';
import {isWithinInterval} from 'date-fns';

import {GrowlService, UserService} from '@app/core';
import {BreadcrumbMain, Format, LabelChangeTransactionType, PrimeNgConfig, TableColumn, Validation} from '@app/shared';
import {DateService} from '@app/shared/services/date.service';


import {AuthorizedDelegate, Delegate, Delegation, DelegationSubType, DelegationType, UserSummary} from './delegation';
import {DelegationDataService} from './delegation-data.service';

@Component({
  selector: 'app-delegation',
  templateUrl: './delegation.component.html',
  styleUrls: ['./delegation.component.css'],
  providers: [ConfirmationService, DelegationDataService]
})
export class DelegationComponent implements OnInit {

  // standard format and validation
  PrimeNgConfig = PrimeNgConfig;
  Format: typeof Format = Format;
  DelegationType: typeof DelegationType = DelegationType;
  DelegationSubType: typeof DelegationSubType = DelegationSubType;
  LabelChangeTransactionType = LabelChangeTransactionType;
  Validation: typeof Validation = Validation;

  public breadcrumb: MenuItem[] = [];

  // authorizedDelegations is an array with DelegationType as the enumerated index
  // the approval array will contain all authorized delegates who are able to approve on user's behalf
  // the submission array will contain all authorized delegates who are able to submit on user's behalf
  authorizedDelegates = [];

  // jC: these will be assigned of Delegation type later

  delegations = [];

  tableColumns: TableColumn[];                  // column headers and fields to display on the table
  menuItems = [];                               // table configuration attributes
  stateOptions: SelectItem[];

  // table: drop down list
  roleDropdown: SelectItem[] = [];              // role dropdown menu
  approvalTypeDropdown: SelectItem[] = [];      // allowed type for approval role
  submissionTypeDropdown: SelectItem[] = [];    // allowed type for submission role
  txnTypeDropdown: SelectItem[] = [];           // transaction type dropdown menu

  // temporary delegation use for add/update table operation
  delegate: Delegate;                           // delegate  for new / update operation
  delegationType: DelegationType;               // delegation type for new / update operation
  newDelegate: boolean;                         // for add, newDelegation === true'

  selectedDelegate: Delegate;                   // pointer to the selected delegation in the table
  suggestedDelegates: UserSummary[];

  // turn on/off dialog for add/update table operation
  displayDialog: boolean;
  visible: boolean = true;
  minDateFrom = new Date();
  minDateThru = new Date();

  dateFrom: Date;

  constructor(private confirmationService: ConfirmationService, private growl: GrowlService, private user: UserService,
              private dateService: DateService, private dataService: DelegationDataService) {
  }

  ngOnInit() {
    this.stateOptions = [{label: 'Off', value: false}, {label: 'On', value: true}];

    this.tableColumns = [
      {field: 'transaction', header: 'Transaction'},
      {field: 'dateFrom', header: 'Start Date'},
      {field: 'dateThru', header: 'End Date'},
      {field: 'userSummary.fullName', header: 'Delegate To'},
      {field: 'action', header: 'Action'},
    ];

    // initialize authorizedDelegation array for approval and submission
    this.authorizedDelegates[DelegationType.Approval] = new AuthorizedDelegate();
    this.authorizedDelegates[DelegationType.Submission] = new AuthorizedDelegate();
    this.authorizedDelegates[DelegationType.Admin] = new AuthorizedDelegate();

    this.delegations[DelegationType.Approval] = new Delegation();
    this.delegations[DelegationType.Submission] = new Delegation();

    this.breadcrumb.push(BreadcrumbMain);
    this.breadcrumb.push({label: 'Delegation', routerLink: '/main/delegation'});

    this.buildMenus();

    this.getAuthDelegateAndMyDelegation();
  }

  /**
   * @desc load my delegation
   */
  getMyDelegation() {
    this.dataService.getDelegation(DelegationType.Self).subscribe(res => {
      this.populateDelegation(res.list);
    });
  }

  /**
   * @desc get my delegation as well as authorized delegates
   * @returns this.authorizedDelegates - authorized approval, submission, and submission executive admin users
   *          this.delegates - user's delegation array used for data table
   */
  getAuthDelegateAndMyDelegation() {
    this.dataService.getDelegationFJ().subscribe(resArray => {
      this.authorizedDelegates[DelegationType.Approval].delegates = plainToClass(UserSummary, resArray[0].users);
      this.authorizedDelegates[DelegationType.Approval].policyId = resArray[0].policyId;
      this.authorizedDelegates[DelegationType.Submission].delegates = plainToClass(UserSummary, resArray[1].users);
      this.authorizedDelegates[DelegationType.Submission].policyId = resArray[1].policyId;
      this.authorizedDelegates[DelegationType.Admin].delegates = plainToClass(UserSummary, resArray[1].execAssistants);
      this.authorizedDelegates[DelegationType.Admin].policyId = resArray[1].policyId;

      this.populateDelegation(resArray[2].list);
    });
  }

  /**
   * @desc build transaction type dropdown menu
   * @returns this.txnTypeDropdown - transaction type dropdwn menu
   */
  buildTxnMenu() {
    this.dataService.getDelegatableTxnsAsDropdown().subscribe(res => {
      this.txnTypeDropdown = res;
    });
  }

  /**
   * @desc build dropdown menus
   * @returns this.roleDropdown - role dropdown menu
   *          this.approvalTypeDropdown - approval dropdown menu
   *          this.submissionTypeDropDown - submission dropdown menu
   *          this.menuItem - dropdown menu to edit/delete a row on data table
   */
  buildMenus() {
    this.buildTxnMenu();

    this.roleDropdown.push({label: "Approval", value: DelegationType.Approval});
    this.roleDropdown.push({label: "Submission", value: DelegationType.Submission});

    this.approvalTypeDropdown.push({label: 'Role', value: DelegationSubType.Role});
    this.approvalTypeDropdown.push({label: 'Transaction', value: DelegationSubType.Transaction});

    this.submissionTypeDropdown.push({label: 'Admin', value: DelegationSubType.Admin});
    this.submissionTypeDropdown.push({label: 'Role', value: DelegationSubType.Role});
    this.submissionTypeDropdown.push({label: 'Transaction', value: DelegationSubType.Transaction});

    this.menuItems[DelegationType.Approval] = [
      {label: 'Edit', icon: 'fa-edit', command: (event) => this.onClickEditDelegate(event.value, DelegationType.Approval)},
      {label: 'Delete', icon: 'fa-close', command: (event) => this.deleteDelegate(event.value, DelegationType.Approval)}];

    this.menuItems[DelegationType.Submission] = [
      {label: 'Edit', icon: 'fa-edit', command: (event) => this.onClickEditDelegate(event.value, DelegationType.Approval)},
      {label: 'Delete', icon: 'fa-close', command: (event) => this.deleteDelegate(event.value, DelegationType.Approval)}];
  }

  /**
   * @desc populate this.delegates to be displayed on the data table.
   * @returns this.approvalDelegation -
   *          this.submissionDelegation -
   *          this.delegates -
   */
  populateDelegation(delegations: Delegation[]) {
    if (delegations) {
      // jC: if this.delegates is not set to [], primeng table does not load the data
      for (let delegation of delegations) {
        this.delegations[delegation.subtype] = plainToClass(Delegation, delegation);
      }
      console.log("populateDelegation: delegations = ");
      console.log(this.delegations);
    }
  }

  /**
   * @desc show a pop up dialog to gather new delegation
   * @returns this.delegate - initialized
   *          this.displayDialog - set to true, so popup dialog is shown to gather new delegation
   */
  onClickAddDelegate(delegationType: DelegationType) {
    this.delegate = new Delegate();
    this.delegationType = delegationType;
    this.newDelegate = true;
    this.displayDialog = true;
  }

  /**
   * @desc edit an existing delegation
   * @param delegate - selected delegate to edit
   * @returns this.leaveRequestToEdit - has copy of the selected leaveRequest
   *          this.minLeaveStarton - set to today's date
   *          this.minLeaveEndOn - set to today's date
   *          displayDialog - set it to true which in turn open up an edit dialog
   */
  onClickEditDelegate(delegate: Delegate, delegationType: DelegationType) {
    if (!delegate) {
      this.growl.add('info', 'Please select a delegation to edit');
    } else {

      console.log("onClickEditDelegate");
      console.log(delegate);
      this.newDelegate = false;
      this.delegationType = delegationType;
      if (!this.delegate) {
        this.delegate = new Delegate();
      }
      Object.assign(this.delegate, delegate);
      this.displayDialog = true;
    }
  }

  searchDelegate(event, delegationType: DelegationType, delegateSubtype: DelegationSubType) {
    let index;
    this.suggestedDelegates = [];

    if (delegateSubtype == DelegationSubType.Admin) {
      index = DelegationType.Admin;
    } else {
      index = delegationType;
    }

    if (this.authorizedDelegates[index].delegates) {
      for (let delegate of this.authorizedDelegates[index].delegates) {
        if (delegate.fullName.toLowerCase().indexOf(event.query.toLowerCase()) >= 0) {
          this.suggestedDelegates.push(delegate);
        }
      }
    }
  }

  /**
   * @desc set delegate.userSummary to null
   * @param delegate - delegate edited
   * @returns delegate - delegate.userSummary set to null
   */
  onChangeDelegateSubtype(delegate: Delegate) {
    // this.suggestedDelegates = [];
    delegate.userSummary = null;
  }

  /**
   * @desc set minDateThru and delgate.dateThru when delegate.dateFrom is set
   * @param delegate - delegate edited; delegate.dateFrom is changed
   * @returns this.minDateThru - set to delegate.dateFrom
   *          delegate.dateThru - set to null if dateFrom is greater than dateThru
   */
  onSelectDateFrom(delegate: Delegate) {
    if (delegate) {
      if (delegate.dateFrom) {
        this.minDateThru = this.dateService.convertStringDateToPCalendarDate(delegate.dateFrom);

      }
      if (delegate.dateFrom && delegate.dateThru && (this.dateService.convertStringDateToUTCDate(delegate.dateFrom).getTime() > this.dateService.convertStringDateToUTCDate(delegate.dateThru).getTime())) {
        delegate.dateThru = null;
      }
    }
  }


  setClass(delegate: Delegate) {
    if (delegate.subtype === DelegationSubType.Admin) {
      delegate['@class'] = 'AdminDelegate';
    } else if (delegate.subtype === DelegationSubType.Role) {
      delegate['@class'] = 'RoleDelegate';
    } else if (delegate.subtype === DelegationSubType.Transaction) {
      delegate['@class'] = 'TxnDelegate';
    }
  }

  isConflictingDelegation(newDelegate: Delegate, delegationType: DelegationType, delgates: Delegate[]) {
    let conflict = false;

    for (let delegate of delgates) {
      if (delegate.id != newDelegate.id && delegate.subtype == newDelegate.subtype) {
        if (delegate.subtype != DelegationSubType.Transaction || delegate.transaction == newDelegate.transaction) {
          console.log(`matching subtype=${delegate.subtype} and/or transaction id=${delegate.transaction}`);
          console.log(`is ${newDelegate.dateFrom} in ${delegate.dateFrom} and ${delegate.dateThru}`);
          console.log(`is ${new Date(newDelegate.dateFrom)} in ${new Date(delegate.dateFrom)} and ${new Date(delegate.dateThru)}`);
          if (isWithinInterval(new Date(newDelegate.dateFrom), {start: new Date(delegate.dateFrom), end: new Date(delegate.dateThru)})) {
            conflict = true;
            break;
          }
          console.log(`is ${newDelegate.dateThru} in ${delegate.dateFrom} and ${delegate.dateThru}`);
          console.log(`is ${new Date(newDelegate.dateThru)} in ${new Date(delegate.dateFrom)} and ${new Date(delegate.dateThru)}`);
          if (isWithinInterval(new Date(newDelegate.dateThru), {start: new Date(delegate.dateFrom), end: new Date(delegate.dateThru)})) {
            conflict = true;
            break;
          }
        }
      }
    }
    return conflict;
  }

  /**
   * @desc add new delegation
   * @param newDelegate - new delegation to be added
   * @returns this.delegates - with newDelegate added
   */
  addDelegate(newDelegate: Delegate, delegationType: DelegationType) {
    // jC: set version, userId, delegationId. use unshift instead of push, so that the new delegate shows up first
    if (newDelegate) {
      // newDelegate.version = 0;
      newDelegate.userId = newDelegate.userSummary.id;

      if (!this.delegations[delegationType]) {
        this.delegations[delegationType] = new Delegation();
      } else {
        if (this.delegations[delegationType].id) {
          newDelegate.delegationId = this.delegations[delegationType].id;
        }
      }

      if (!this.delegations[delegationType].policyId) {
        this.delegations[delegationType].policyId = this.authorizedDelegates[delegationType].policyId;
      }
      if (!this.delegations[delegationType].subtype) {
        this.delegations[delegationType].subtype = delegationType;
      }


      if (!this.isConflictingDelegation(newDelegate, delegationType, this.delegations[delegationType].delegates)) {
        this.delegations[delegationType].delegates.unshift(newDelegate);
        console.log("addDelegate: this.delegations[${delegationType}] = ");
        console.log(this.delegations[delegationType]);
        this.displayDialog = false;
        this.submitDelegation();
      } else {
        this.growl.add("warn", "Conflicting delegation exists. Please update the delegation and then resubmit.");
      }


    } else {
      console.error("Cannot update null delegate");
    }
  }

  /**
   * @desc update delegation
   * @param updatedDelegate - updated delegation
   * @returns this.delegates - with updatedDelegate
   */
  updateDelegate(updatedDelegate: Delegate, delegationType: DelegationType) {
    if (updatedDelegate) {
      console.log(updatedDelegate);
      console.log(this.delegations[delegationType].delegates);
      if (this.delegations[delegationType].delegates) {
        let index = this.delegations[delegationType].delegates.findIndex(delegate => delegate.id == updatedDelegate.id);
        if (index >= 0) {
          if (!this.isConflictingDelegation(updatedDelegate, delegationType, this.delegations[delegationType].delegates)) {
            Object.assign(this.delegations[delegationType].delegates[index], updatedDelegate);
            this.displayDialog = false;
            this.submitDelegation();
          } else {
            this.growl.add("warn", "Conflicting delegation exists. Please update the delegation and then resubmit.");
          }
        } else {
          console.error("Updating delegation failed. delegate = ");
          console.log(updatedDelegate);
        }
      }
    } else {
      console.error("Cannot update null delegate");
    }
  }

  /**
   * @desc enalbe/disable delegation; confirm changes and revert back if user rejects it
   * @returns this.delegates - specified delegation turned on/off
   */
  toggleDelegation(enableDelegation: boolean, delegationType: DelegationType) {
    console.log("toggleDelegation: enable = , delegationType = ");
    console.log(enableDelegation);
    console.log(delegationType);
    let msg: string;

    if (enableDelegation) {
      msg = "enable";
    } else {
      msg = "disable";
    }

    this.confirmationService.confirm({
      message: `Are you sure that you want to ${msg} this delegation?`,
      header: 'Enable/Disable Delegation',
      icon: 'fa fa-question-circle',
      accept: () => {
        if (delegationType) {
          // ToDo: enable this once backend is available
          this.submitDelegation();
          console.log("submit delegation on/off");
        }
      },
      reject: () => {
        this.delegations[delegationType].delegationOn = !this.delegations[delegationType].delegationOn;
        console.log("reverting delegation on/off");
      }
    });

  }

  /**
   * @desc close the delegation update window
   */
  cancel() {
    this.delegate = null;
    this.displayDialog = false;
  }

  /**
   * @desc delete a chosen delegate/delegation
   * @param delegate - delegation to be deleted
   */
  deleteDelegate(delegate: Delegate, delegationType: DelegationType) {
    // ToDo add confirmation dialog

    let index = this.delegations[delegationType].delegates.findIndex(delegate => delegate.id == delegate.id);
    if (index >= 0) {
      this.confirmationService.confirm({
        message: `Are you sure that you want to delete this delegation?`,
        header: 'Delete Delegation',
        icon: 'fa fa-question-circle',
        accept: () => {
          this.delegations[delegationType].delegates = this.delegations[delegationType].delegates.filter((val, i) => i != index);
          this.submitDelegation();
        },
        reject: () => {
        }
      });
    }
  }

  /**
   * @desc submit delegation change(s) to the backend
   */
  submitDelegation() {
    let delegations: Delegation[] = [];

    if (this.delegations[DelegationType.Approval].delegates && this.delegations[DelegationType.Approval].delegates.length > 0) {
      delegations.push(this.delegations[DelegationType.Approval]);
    }
    if (this.delegations[DelegationType.Submission].delegates && this.delegations[DelegationType.Submission].delegates.length > 0) {
      delegations.push(this.delegations[DelegationType.Submission]);
    }

    this.dataService.saveDelegation(classToPlain(delegations, {excludePrefixes: ['_$visited']}))
      .subscribe(res => {
        this.growl.add('success', 'Delegation updated successfully.');
        this.populateDelegation(res.list);
      });

  }
}

